import { zodResolver } from '@hookform/resolvers/zod'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import styles from './../../Dialog.module.sass'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'
import { EditInjuredDataSchema } from './EditInjuredDataSchema'
import { useDeepCompareEffect } from 'react-use'
import HFValidatePostalCodeInput from 'components/HFInputs/HFValidatePostalCodeInput'
import HFDatepicker from 'components/HFInputs/HFDatepicker'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import HFDropdown from 'components/HFInputs/HFDropdown'
import { useFetchList } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const EditInjuredDataDialog = (props) => {
	const { data, dismiss, resolve } = props
	const { injured, processId } = data
	const { i18n, logout, pushNotification } = useGlobal()
	const [defaultValues, setDefaultValues] = useState(null)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)

	const labels = {
		name: i18n`Name`,
		birthDate: i18n`Birth date`,
		nif: i18n`nif`,
		profession: i18n`Profession`,
		address: i18n`Address`,
		postalCode: i18n`Postal code`,
		email: i18n`Email`,
		phoneNumber: i18n`Phone nr.`,
		telephone: i18n`Telephone nr.`,
	}

	const {
		contents: [professions],
	} = useFetchList(
		[`metadata/professions`],

		[
			(json) => {
				return {
					value: json?.id,
					text: json?.name,
				}
			},
		]
	)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(EditInjuredDataSchema(i18n)),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, reset } = methods

	useEffect(() => {
		if (
			(data.formState || defaultValues) &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(data.formState ? data.formState : defaultValues)
		}
	}, [
		reset,
		defaultValues,
		data.formState,
		formState.isDirty,
		formState.submitCount,
	])

	useDeepCompareEffect(() => {
		let defaultValues = {}

		if (injured?.name) {
			defaultValues = {
				...defaultValues,
				name: injured.name,
			}
		}

		if (injured?.address) {
			defaultValues = {
				...defaultValues,
				address: injured.address,
			}
		}

		if (injured?.nif) {
			defaultValues = {
				...defaultValues,
				nif: injured.nif,
			}
		}

		if (injured?.professionId) {
			defaultValues = {
				...defaultValues,
				profession: injured?.professionId,
			}
		}

		if (injured?.birthday) {
			defaultValues = {
				...defaultValues,
				birthDate:
					injured?.birthdayInfo?.birthdayDateMoment?.toISOString(),
			}
		}

		if (injured?.postalCode) {
			defaultValues = {
				...defaultValues,
				postalCode: injured?.postalCode,
			}
		}

		if (injured?.contacts?.email) {
			defaultValues = {
				...defaultValues,
				email: injured?.contacts?.email,
			}
		}

		if (injured?.contacts?.mobile) {
			defaultValues = {
				...defaultValues,
				phoneNumber: parseInt(
					injured?.formattedPhoneContacts?.mobile.split(' ').join('')
				),
			}
		}

		if (injured?.contacts?.telephone) {
			defaultValues = {
				...defaultValues,
				telephone: parseInt(
					injured?.formattedPhoneContacts?.telephone
						.split(' ')
						.join('')
				),
			}
		}

		setDefaultValues({ ...defaultValues })
	}, [injured ?? {}])

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}

		const dataToSubmit = { ...formData }
		if (dataToSubmit.birthDate) {
			const date = new Date(dataToSubmit.birthDate)
			dataToSubmit.birthDate = `${date.getFullYear()}-${String(
				date.getMonth() + 1
			).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
		}

		console.log('Chosen dataToSubmit.birthDate:', dataToSubmit.birthDate)

		const endpoint = `${API_URL}/api/clinical_processes/${processId}/injured/${injured?.id}`

		setError(null)
		setSubmitting('submitting')

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: dataToSubmit,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				pushNotification(
					undefined,
					'success',
					i18n`Injured data updated successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_injured_data_edit'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['data-editing-form'] || ''}`}
					disabled={submitting}
				>
					<div className={styles['form-input-group']}>
						<VStack>
							<h5>{labels.name}</h5>
							<HFTextInput name='name' type='text' />
						</VStack>

						<VStack>
							<h5>{labels.birthDate}</h5>
							<HFDatepicker
								name='birthDate'
								type='date'
								initialDate={
									injured?.birthdayInfo?.birthdayDateMoment
								}
							/>
						</VStack>

						<VStack>
							<h5>{labels.nif}</h5>
							<HFTextInput name='nif' type='number' />
						</VStack>

						<VStack>
							<h5>{labels.profession}</h5>
							<HFDropdown
								name='profession'
								options={professions}
								optionsAlign='left'
								searchable={true}
								noData={i18n`No results`}
								type='number'
							/>
						</VStack>
						<VStack>
							<h5>{labels.address}</h5>
							<HFTextInput name='address' />
						</VStack>
						<VStack>
							<h5>{labels.postalCode}</h5>
							<HFValidatePostalCodeInput
								className={styles['postal-code']}
								name='postalCode'
								inputMask='9999-999'
								type='text'
								placeholder='____-___'
							/>
						</VStack>
						<VStack>
							<h5>{labels.phoneNumber}</h5>
							<HFTextInput name='phoneNumber' type='number' />
						</VStack>
						<VStack>
							<h5>{labels.telephone}</h5>
							<HFTextInput name='telephone' type='number' />
						</VStack>
						<VStack>
							<h5>{labels.email}</h5>
							<HFTextInput name='email' type='text' />
						</VStack>
					</div>
				</form>
			</FormProvider>

			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_injured_data_edit`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Save`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</>
	)
}

export default EditInjuredDataDialog
